"use client";

import { useEffect, useRef, useState } from "react";
import {
  Checkbox,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  buildMonthly,
  buildSignIn,
  buildWeekly,
} from "@/components/tools/printables/attendance-build";
import {
  PaperControls,
  PRINTABLES_COMMON_STRINGS,
  PrintablePages,
  PrintBar,
  useTextMeasure,
} from "@/components/tools/printables/page-kit";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  parseDaysOff,
  parseRoster,
} from "@/lib/tool-kit/printables/attendance";
import {
  addDays,
  clampYear,
  daysInMonth,
  monthName,
  parseIsoDate,
  weekday,
} from "@/lib/tool-kit/printables/calendar";
import {
  type Orientation,
  type PaperSize,
  pageGeometry,
} from "@/lib/tool-kit/printables/page";

/** English strings. A translated content file overrides these through `ui`. */
export const ATTENDANCE_SHEET_GENERATOR_STRINGS = {
  ...PRINTABLES_COMMON_STRINGS,
  sampleRoster: "Ava Chen\nBen Okafor\nChloe Martin\nDiego Santos\nEmma Novak",
  rosterLabel: "Class list — one name per line",
  rosterHint: "Leave it empty for a blank register to fill in by hand.",
  typeLabel: "Sheet",
  types: {
    monthly: "Monthly register",
    weekly: "Weekly register",
    signin: "Sign-in sheet",
  },
  titles: {
    monthly: "Attendance",
    weekly: "Weekly Attendance",
    signin: "Sign-In Sheet",
  },
  titleLabel: "Title",
  monthLabel: "Month",
  yearLabel: "Year",
  saturdays: "Include Saturdays",
  sundays: "Include Sundays",
  daysOffLabel: "Days off (dates in the month)",
  daysOffHint:
    "For example: 4, 11-13, 25. Leave empty if every school day counts.",
  daysOffModeLabel: "Days off in the table",
  daysOffModes: { shade: "Shade the columns", remove: "Leave the columns out" },
  totals: "Absent and tardy total columns",
  rowsLabel: "Rows on the sheet",
  rowsHint:
    "Blank numbered rows fill the sheet up to this number, for late arrivals.",
  weekDateLabel: "A date in the week (optional)",
  weekDateHint:
    "Fills in the dates on the column headings. Leave empty to write them in.",
  halves: "Split each day into morning and afternoon",
  keyShow: "Print a key at the bottom",
  numberHead: "No.",
  nameHead: "Name",
  absentHead: "Absent",
  tardyHead: "Tardy",
  signatureHead: "Signature",
  timeInHead: "Time in",
  timeOutHead: "Time out",
  amHead: "AM",
  pmHead: "PM",
  classLabel: "Class",
  teacherLabel: "Teacher",
  dateLabel: "Date",
  eventLabel: "Class or event",
  keyText: "Key:  / present    A absent    T tardy    E excused",
  previewLabel: "Attendance sheet preview",
} as const;

type SheetType = keyof typeof ATTENDANCE_SHEET_GENERATOR_STRINGS.types;

const ROW_CHOICES = ["10", "15", "20", "25", "30", "35", "40"];
const DEFAULT_YEAR = 2026;
const DEFAULT_MONTH = 8;

/** The Monday on or before a date, as a civil date. */
function mondayOf(date: { year: number; month: number; day: number }) {
  const back = (weekday(date.year, date.month, date.day) + 6) % 7;
  return addDays(date, -back);
}

export default function AttendanceSheetGeneratorWidget() {
  const strings = useToolUi(ATTENDANCE_SHEET_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const measure = useTextMeasure();
  const svgRef = useRef<SVGSVGElement>(null);
  const [type, setType] = useState<SheetType>("monthly");
  const [roster, setRoster] = useState<string>(strings.sampleRoster);
  const [year, setYear] = useState(DEFAULT_YEAR);
  const [yearText, setYearText] = useState(String(DEFAULT_YEAR));
  const [month, setMonth] = useState(DEFAULT_MONTH);
  const [saturdays, setSaturdays] = useState(false);
  const [sundays, setSundays] = useState(false);
  const [daysOff, setDaysOff] = useState("");
  const [removeOff, setRemoveOff] = useState(false);
  const [totals, setTotals] = useState(true);
  const [rows, setRows] = useState("30");
  const [titles, setTitles] = useState<Record<SheetType, string>>({
    monthly: strings.titles.monthly,
    weekly: strings.titles.weekly,
    signin: strings.titles.signin,
  });
  const [weekDate, setWeekDate] = useState("");
  const [halves, setHalves] = useState(false);
  const [showKey, setShowKey] = useState(true);
  const [paper, setPaper] = useState<PaperSize>("letter");
  const [orientation, setOrientation] = useState<Orientation>("landscape");

  useEffect(() => {
    const now = new Date();
    setYear(now.getFullYear());
    setYearText(String(now.getFullYear()));
    setMonth(now.getMonth());
  }, []);

  function chooseType(next: SheetType) {
    setType(next);
    setOrientation(next === "monthly" ? "landscape" : "portrait");
  }

  function editYear(text: string) {
    setYearText(text);
    const value = parseIntegerInput(text);
    if (value !== null && value >= 1900 && value <= 2200) {
      setYear(clampYear(value));
    }
  }

  const geometry = pageGeometry(paper, orientation);
  const people = parseRoster(roster);
  const minRows = Number(rows);
  const names = [
    ...people,
    ...Array.from({ length: Math.max(0, minRows - people.length) }, () => ""),
  ];
  const common = {
    geometry,
    measure,
    locale,
    strings,
    title: titles[type].trim(),
    names,
    showKey,
  };
  let pages: React.ReactNode[] = [];
  if (type === "monthly") {
    pages = buildMonthly({
      ...common,
      year,
      month,
      totals,
      days: {
        saturdays,
        sundays,
        daysOff: parseDaysOff(daysOff, daysInMonth(year, month)),
        removeOff,
      },
    });
  } else if (type === "weekly") {
    const date = parseIsoDate(weekDate);
    pages = buildWeekly({
      ...common,
      monday: date ? mondayOf(date) : null,
      saturdays,
      sundays,
      halves,
    });
  } else {
    pages = buildSignIn({ ...common, showKey: false });
  }

  return (
    <div className="space-y-6 print:m-0 print:space-y-0">
      <div className="space-y-5 print:hidden">
        <TextAreaField
          hint={strings.rosterHint}
          id="as-roster"
          label={strings.rosterLabel}
          onChange={setRoster}
          prose
          rows={6}
          value={roster}
        />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <SelectField
            id="as-type"
            label={strings.typeLabel}
            onChange={chooseType}
            options={(["monthly", "weekly", "signin"] as const).map(
              (value) => ({ value, label: strings.types[value] })
            )}
            value={type}
          />
          <TextField
            id="as-title"
            label={strings.titleLabel}
            onChange={(value) =>
              setTitles((previous) => ({ ...previous, [type]: value }))
            }
            value={titles[type]}
          />
          <SelectField
            hint={strings.rowsHint}
            id="as-rows"
            label={strings.rowsLabel}
            onChange={setRows}
            options={ROW_CHOICES.map((value) => ({ value, label: value }))}
            value={rows}
          />
          {type === "monthly" ? (
            <>
              <SelectField
                id="as-month"
                label={strings.monthLabel}
                onChange={(value) => setMonth(Number(value))}
                options={Array.from({ length: 12 }, (_, index) => ({
                  value: String(index),
                  label: monthName(locale, index),
                }))}
                value={String(month)}
              />
              <TextField
                id="as-year"
                label={strings.yearLabel}
                numeric
                onChange={editYear}
                value={yearText}
              />
              <TextField
                hint={strings.daysOffHint}
                id="as-days-off"
                label={strings.daysOffLabel}
                onChange={setDaysOff}
                value={daysOff}
              />
              <SelectField
                id="as-days-off-mode"
                label={strings.daysOffModeLabel}
                onChange={(value) => setRemoveOff(value === "remove")}
                options={(["shade", "remove"] as const).map((value) => ({
                  value,
                  label: strings.daysOffModes[value],
                }))}
                value={removeOff ? "remove" : "shade"}
              />
            </>
          ) : null}
          {type === "weekly" ? (
            <TextField
              hint={strings.weekDateHint}
              id="as-week-date"
              label={strings.weekDateLabel}
              onChange={setWeekDate}
              type="date"
              value={weekDate}
            />
          ) : null}
          <PaperControls
            idPrefix="as"
            onOrientation={setOrientation}
            onPaper={setPaper}
            orientation={orientation}
            paper={paper}
            strings={strings}
          />
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {type === "signin" ? null : (
            <>
              <Checkbox
                checked={saturdays}
                label={strings.saturdays}
                onChange={setSaturdays}
              />
              <Checkbox
                checked={sundays}
                label={strings.sundays}
                onChange={setSundays}
              />
            </>
          )}
          {type === "monthly" ? (
            <Checkbox
              checked={totals}
              label={strings.totals}
              onChange={setTotals}
            />
          ) : null}
          {type === "weekly" ? (
            <Checkbox
              checked={halves}
              label={strings.halves}
              onChange={setHalves}
            />
          ) : null}
          {type === "signin" ? null : (
            <Checkbox
              checked={showKey}
              label={strings.keyShow}
              onChange={setShowKey}
            />
          )}
        </div>
        <PrintBar
          fileName={`attendance-${type}-${paper}`}
          pageTotal={pages.length}
          paper={paper}
          strings={strings}
          svgRef={svgRef}
        />
      </div>
      <PrintablePages
        className={orientation === "landscape" ? "max-w-3xl" : "max-w-xl"}
        geometry={geometry}
        label={strings.previewLabel}
        pages={pages}
        svgRef={svgRef}
      />
    </div>
  );
}
